import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Divider, Stack, Typography } from '@mui/material';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import { RESET_PASSWORD_MUTATION } from '../../gql';
import { MIN_PASSWORD_LENGTH, useYupValidationResolver } from '../../helpers';
import {
  Button,
  FieldLabel,
  FormField,
  SurfaceTextField,
} from '../../components/design-system';

const submitButtonStyles = {
  borderRadius: 2,
  fontSize: '1rem',
};

const validationSchema = Yup.object().shape({
  password: Yup.string()
    .required('Password is mandatory field.')
    .min(
      MIN_PASSWORD_LENGTH,
      `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`
    ),
  confirmPassword: Yup.string()
    .required('Please, confirm password.')
    .oneOf([Yup.ref('password')], 'Passwords do not match.'),
});

interface PasswordUpdateFormProps {
  id: string;
  token: string;
}

const PasswordUpdateForm = ({ id, token }: PasswordUpdateFormProps) => {
  const [errors, setErrors] = useState<Array<{ error: string }>>([]);
  const [isComplete, setIsComplete] = useState(false);
  const resolver = useYupValidationResolver(validationSchema, setErrors);
  const { register, handleSubmit } = useForm({ resolver });

  const [resetPassword, { loading }] = useMutation(RESET_PASSWORD_MUTATION);

  const onSubmit = async (values: { password: string }) => {
    const { data } = await resetPassword({
      variables: {
        id,
        token,
        newPassword: values.password,
      },
    });

    if (data && !data.resetPassword.errors) {
      setIsComplete(true);
    } else {
      setErrors(data!.resetPassword!.errors);
    }
  };

  if (isComplete) {
    return (
      <Stack width="100%" spacing={2}>
        <Typography>
          Your password has been successfully updated. You can now sign in with
          your new password.
        </Typography>
        <Divider />
        <Stack width="100%" alignItems="center">
          <Typography variant="caption">
            <Link to="/signin" style={{ color: '#FF0070' }}>
              Sign-in
            </Link>
          </Typography>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      spacing={2}
      width="100%"
    >
      <FormField width="100%">
        <FieldLabel htmlFor="password">New password</FieldLabel>
        <SurfaceTextField
          fullWidth
          id="password"
          surface="sunken"
          type="password"
          {...register('password')}
        />
      </FormField>
      <FormField width="100%">
        <FieldLabel htmlFor="confirmPassword">Repeat new password</FieldLabel>
        <SurfaceTextField
          fullWidth
          id="confirmPassword"
          surface="sunken"
          type="password"
          {...register('confirmPassword')}
        />
      </FormField>
      <Divider color="transparent" />
      <Button
        type="submit"
        fullWidth
        sx={submitButtonStyles}
        disabled={loading}
      >
        Change password
      </Button>
      <Divider />
      <Stack width="100%">
        {errors.length > 0 && (
          <Stack>
            {errors.map((item: { error: string }) => (
              <Typography color="red">{item.error}</Typography>
            ))}
          </Stack>
        )}
      </Stack>
      <Stack width="100%" alignItems="center">
        <Typography variant="caption">
          <Link to="/" style={{ color: '#D76B90' }}>
            Home
          </Link>
        </Typography>
      </Stack>
    </Stack>
  );
};

export { PasswordUpdateForm };
